/** Carrega e renderiza o PDF com pdf.js (ADR-009).
 *
 * O PDF é **conteúdo passivo**: o pdf.js desenha as páginas em canvas e expõe a
 * camada de texto, sem executar o JS embutido no arquivo. Por isso ele roda na
 * origem da app, e não na origem sandbox do ADR-001 — o que dispensa Service
 * Worker por origem e ponte postMessage.
 *
 * A renderização é LAZY: só a página visível (e a vizinha) é desenhada, senão
 * um PDF de 300 páginas trava a UI.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

export interface PdfDocumentLike {
  numPages: number
  getPage: (page: number) => Promise<PdfPageLike>
  destroy?: () => void
}

export interface PdfViewportLike {
  width: number
  height: number
  scale: number
}

export interface PdfTextItemLike {
  str: string
}

export interface PdfTextContentLike {
  items: PdfTextItemLike[]
}

export interface PdfPageLike {
  getViewport: (params: { scale: number }) => PdfViewportLike
  render: (params: {
    canvasContext: CanvasRenderingContext2D
    viewport: PdfViewportLike
    transform?: number[]
  }) => { promise: Promise<void>; cancel?: () => void }
  getTextContent: () => Promise<PdfTextContentLike>
}

/** Tarefa de montagem da camada de texto — cancelável, como o render do canvas. */
export interface TextLayerTask {
  promise: Promise<void>
  cancel: () => void
}

export type TextLayerRenderer = (params: {
  container: HTMLElement
  textContent: PdfTextContentLike
  viewport: PdfViewportLike
}) => TextLayerTask

export type PdfLoader = (url: string) => Promise<PdfDocumentLike>

/** Montagem padrão da camada de texto — a classe `TextLayer` do pdf.js.
 *
 * ⚠️ Na 4.x `renderTextLayer` foi REMOVIDO; quem procurar por ele não acha. E o
 * import é dinâmico pelo mesmo motivo do loader: o bundle toca
 * DOMMatrix/Path2D/OffscreenCanvas no topo do módulo e derruba a suíte inteira
 * em jsdom, na IMPORTAÇÃO — o erro apareceria num arquivo que não tem culpa.
 */
export const defaultTextLayerRenderer: TextLayerRenderer = ({
  container,
  textContent,
  viewport,
}) => {
  let camada: { render: () => Promise<void>; cancel: () => void } | null = null
  const promise = import('pdfjs-dist').then(async ({ TextLayer }) => {
    camada = new TextLayer({
      textContentSource: textContent as never,
      container,
      viewport: viewport as never,
    })
    await camada.render()
  })
  return {
    promise,
    cancel: () => camada?.cancel(),
  }
}

/** Carregador padrão: pdf.js com worker, versão pinada pelo package.json. */
export const defaultLoader: PdfLoader = async (url) => {
  const pdfjs = await import('pdfjs-dist')
  pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
  ).toString()
  const task = pdfjs.getDocument({
    url,
    // Defesa em profundidade sobre a sanitização da ingestão: mesmo que algo
    // passe, o renderizador não executa script nem abre recurso externo.
    isEvalSupported: false,
    disableAutoFetch: true,
    disableStream: true,
    rangeChunkSize: 262144,
  })
  return (await task.promise) as unknown as PdfDocumentLike
}

interface UsePdfDocumentOptions {
  url: string | null
  loader?: PdfLoader
}

export function usePdfDocument({ url, loader = defaultLoader }: UsePdfDocumentOptions) {
  /** Resultado do carregamento, CARIMBADO com a URL que o produziu.
   *
   * Guardar a URL junto é o que permite derivar `loading` e `error` em vez de
   * escrevê-los por `setState` no corpo do efeito — três `setState` ali geram
   * render em cascata, e o linter tem razão em recusar. Como bônus, `doc`
   * continua sendo o documento ANTERIOR enquanto o novo carrega: numa renovação
   * de URL pré-assinada, o aluno segue lendo a página que está na tela em vez de
   * ver o livro sumir.
   */
  const [resultado, setResultado] = useState<{
    url: string
    doc: PdfDocumentLike | null
    erro: Error | null
  } | null>(null)
  const renderTask = useRef<{ cancel?: () => void } | null>(null)

  const doc = url ? (resultado?.doc ?? null) : null
  const numPages = doc?.numPages ?? 0
  const loading = url !== null && resultado?.url !== url
  const error = resultado?.url === url ? (resultado?.erro ?? null) : null

  useEffect(() => {
    if (!url) return
    let cancelado = false

    loader(url)
      .then((carregado) => {
        if (cancelado) {
          carregado.destroy?.()
          return
        }
        // ⚠️ Destruir o ANTERIOR ao substituir. Sem isto cada troca de URL vaza
        // um PDFDocumentProxy com o worker e os caches de página — raro quando a
        // URL era eterna, diário depois que a renovação pré-assinada entra.
        setResultado((atual) => {
          if (atual?.doc && atual.doc !== carregado) atual.doc.destroy?.()
          return { url, doc: carregado, erro: null }
        })
      })
      .catch((err: unknown) => {
        if (cancelado) return
        setResultado({
          url,
          doc: null,
          erro: err instanceof Error ? err : new Error('Falha ao abrir o PDF'),
        })
      })

    return () => {
      cancelado = true
    }
  }, [url, loader])

  // Desmontar o leitor também libera o worker do pdf.js. A ref espelha o estado
  // porque o cleanup de desmontagem não deve depender de `resultado`, senão ele
  // roda a cada troca de documento em vez de só na saída.
  const docAtual = useRef<PdfDocumentLike | null>(null)
  useEffect(() => {
    docAtual.current = resultado?.doc ?? null
  }, [resultado])
  useEffect(
    () => () => {
      docAtual.current?.destroy?.()
    },
    [],
  )

  const renderPage = useCallback(
    async (page: number, canvas: HTMLCanvasElement, scale = 1.4): Promise<void> => {
      if (!doc || page < 1 || page > doc.numPages) return
      // Cancela o render anterior: virar página rápido não pode empilhar trabalho.
      renderTask.current?.cancel?.()

      const pdfPage = await doc.getPage(page)
      const viewport = pdfPage.getViewport({ scale })
      const context = canvas.getContext('2d')
      if (!context) return

      canvas.width = viewport.width
      canvas.height = viewport.height
      const task = pdfPage.render({ canvasContext: context, viewport })
      renderTask.current = task
      await task.promise
    },
    [doc],
  )

  return { doc, numPages, loading, error, renderPage }
}
